import firebase from '../config/firebase.config';
import momentTz from 'moment-timezone';

const database = firebase.firestore();

const MEMBER_DATA_COLLECTION = 'memberData';
const ACCUMULATOR_SUBCOLLECTION = 'accumulator';
// Matches PERIOD_ID_KEY.day in functions/accumulator-helpers.js — the
// innermost collection actually holding a per-day document, for both the
// permanent per-member day record (memberData/{teamId}/daysData/{userUID}/dateId/{dateId})
// and the team rollup (teamDatabase/{teamId}/consolidatedData/days/dateId/{dateId}).
// They share this collection name coincidentally, not by relation — the
// team-rollup shape carries no `teamId` field, so it can never satisfy the
// `where('teamId', '==', teamId)` filter below and is never a false match.
const DAY_PERIOD_COLLECTION = 'dateId';

const CHECK_IN_TYPES = ['late', 'early'];

// dateId is always computed in the team's own timezone (matching the
// backend's getPeriodIds) — never the browser's local timezone, since a
// manager and their team may not share one.
function getTodayDateId(timeZone) {
  return timeZone && momentTz.tz.zone(timeZone)
    ? momentTz().tz(timeZone).format('YYYY-MM-DD')
    : momentTz.utc().format('YYYY-MM-DD');
}

function getLastNDateIds(dateId, timeZone, n = 7) {
  const anchor =
    timeZone && momentTz.tz.zone(timeZone)
      ? momentTz.tz(dateId, 'YYYY-MM-DD', timeZone)
      : momentTz.utc(dateId, 'YYYY-MM-DD');
  const ids = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    ids.push(anchor.clone().subtract(i, 'days').format('YYYY-MM-DD'));
  }
  return ids;
}

async function getTeamDailyRollup(teamId, dateId) {
  try {
    const snap = await database
      .collection('teamDatabase')
      .doc(teamId)
      .collection('consolidatedData')
      .doc('days')
      .collection(DAY_PERIOD_COLLECTION)
      .doc(dateId)
      .get();
    return { success: true, data: snap.exists ? snap.data() : null };
  } catch (error) {
    console.error('getTeamDailyRollup error:', error);
    return { success: false, data: null, error: error.message };
  }
}

// Fetches several days of rollups in parallel — one small doc per day,
// regardless of team size, so a 7-day trend chart never scales with headcount.
async function getTeamRollupRange(teamId, dateIds) {
  try {
    const results = await Promise.all(
      dateIds.map((dateId) => getTeamDailyRollup(teamId, dateId))
    );
    return {
      success: true,
      data: dateIds.map((dateId, i) => ({
        dateId,
        ...(results[i].data || {}),
      })),
    };
  } catch (error) {
    console.error('getTeamRollupRange error:', error);
    return { success: false, data: [] };
  }
}

// True only if the login-alert trigger actually ran for this team on this
// day (i.e. lateCount/earlyCount were ever written, even as 0) — NOT the
// same thing as "nobody was late or early". presentCount on the rollup doc
// comes from the older, pre-existing productivity-tracking job and exists
// for every day regardless of whether login alerts were configured, so it
// must never be used to backfill an "everyone was on time" assumption for a
// day that has no real late/early data at all.
const hasLoginAlertData = (rollup) =>
  rollup?.lateCount !== undefined || rollup?.earlyCount !== undefined;

/**
 * Paginated attendance exceptions (late/early check-ins) for one team+day.
 * Sourced from the live in-progress accumulator when dateId is today (in the
 * team's own timezone) since that day hasn't been flushed to permanent
 * storage yet, and from permanent daysData (via a collection-group query,
 * since one query must span every employee in the team) for any past day.
 * Cost is bounded by `pageSize`, never by team size — this never fetches
 * more records than are actually being displayed.
 */
async function getAttendanceExceptions(
  teamId,
  dateId,
  { type = 'all', pageSize = 20, cursor = null, timeZone } = {}
) {
  try {
    const isToday = dateId === getTodayDateId(timeZone);
    const typeFilter = type === 'all' ? CHECK_IN_TYPES : [type];

    // An employee who hasn't had any session activity yet today still has
    // YESTERDAY's full day object sitting in their accumulator untouched
    // (it only resets on their first ping of the new day) — so this must
    // also filter on day.dateId, or a person late/early yesterday would
    // wrongly appear in today's exceptions before they've even checked in.
    let query = isToday
      ? database
          .collection(MEMBER_DATA_COLLECTION)
          .doc(teamId)
          .collection(ACCUMULATOR_SUBCOLLECTION)
          .where('day.dateId', '==', dateId)
          .where('day.checkInType', 'in', typeFilter)
      : database
          .collectionGroup(DAY_PERIOD_COLLECTION)
          .where('teamId', '==', teamId)
          .where('dateId', '==', dateId)
          .where('checkInType', 'in', typeFilter);

    query = query.limit(pageSize);
    if (cursor) query = query.startAfter(cursor);

    const snapshot = await query.get();
    const records = snapshot.docs.map((doc) => {
      const d = doc.data();
      const day = isToday ? d.day || {} : d;
      return {
        userUID: isToday ? doc.id : d.userUID,
        checkInDiffMinutes: day.checkInDiffMinutes ?? null,
        checkInType: day.checkInType ?? null,
        severity: day.severity ?? null,
        firstLoginAt: day.firstLoginAt ?? null,
        policySnapshot: day.policySnapshot ?? null,
      };
    });

    // Cross-reference the unified alerts collection for this page of rows
    // only (bounded by pageSize, same deterministic ID the backend writes)
    // to show whether an alert was actually sent/acknowledged for each row.
    // A missing alert doc means loginAlertEnabled was off that day, not an
    // error — surfaced as `alertStatus: null` for the view to label plainly.
    if (records.length > 0) {
      const alertSnaps = await Promise.all(
        records.map((r) => {
          const alertType =
            r.checkInType === 'late' ? 'lateLogin' : 'earlyLogin';
          return database
            .collection('teamDatabase')
            .doc(teamId)
            .collection('alerts')
            .doc(`${r.userUID}_${dateId}_${alertType}`)
            .get();
        })
      );
      alertSnaps.forEach((snap, i) => {
        records[i].alertStatus = snap.exists ? snap.data().status : null;
      });
    }

    return {
      success: true,
      data: records,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === pageSize,
    };
  } catch (error) {
    console.error('getAttendanceExceptions error:', error);
    return {
      success: false,
      data: [],
      lastDoc: null,
      hasMore: false,
      error: error.message,
    };
  }
}

// Employee name/email lookup for enriching exception rows client-side — a
// single doc read (teamDatabase/{teamId} is already fetched all over the
// app for this exact purpose), not a per-row fetch.
async function getTeamMemberDirectory(teamId) {
  try {
    const snap = await database.collection('teamDatabase').doc(teamId).get();
    return {
      success: true,
      data: snap.exists ? snap.data()?.memberDetails || {} : {},
    };
  } catch (error) {
    console.error('getTeamMemberDirectory error:', error);
    return { success: false, data: {} };
  }
}

export default {
  getTodayDateId,
  getLastNDateIds,
  getTeamDailyRollup,
  getTeamRollupRange,
  getAttendanceExceptions,
  getTeamMemberDirectory,
  hasLoginAlertData,
};
